/**
 * Search & Discovery recommendations seed: recently-viewed products.
 *
 * Two responsibilities:
 *   1. On PDP, capture the current product_id and push it to localStorage
 *      (dedup, newest-first, capped at MAX_ITEMS).
 *   2. On any page with <product-recommendations data-seed-source="recently-viewed">,
 *      overwrite data-product-id with the most recent localStorage entry —
 *      BEFORE the custom element upgrades, so connectedCallback reads the new seed.
 *
 * Script ordering: load BEFORE global.js (where customElements.define for
 * <product-recommendations> runs). theme.liquid places the <script defer> tag
 * accordingly.
 *
 * Exposes for tests:
 *   window.LibertaRecentlyViewed = { STORAGE_KEY, MAX_ITEMS, record, getList,
 *                                    clear, applySeedOverride }
 */
(function () {
  const STORAGE_KEY = 'liberta:recently_viewed_products';
  // 商品ID → handle のマップ (別キー)。ID 配列(STORAGE_KEY)の形式は互換維持し、
  // 「最近見た商品」をカード描画するための handle はこちらに持つ。
  const HANDLE_KEY = 'liberta:recently_viewed_handles';
  const MAX_ITEMS = 10;

  function getList() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.map(String) : [];
    } catch (e) {
      return [];
    }
  }

  function getHandleMap() {
    try {
      const raw = localStorage.getItem(HANDLE_KEY);
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch (e) {
      return {};
    }
  }

  // [{ id, handle }] (新しい順)。handle 不明な項目は handle: null。
  function getEntries() {
    const map = getHandleMap();
    return getList().map((id) => ({ id: id, handle: map[id] || null }));
  }

  function record(productId, handle) {
    if (productId === null || productId === undefined || productId === '') return;
    const id = String(productId);
    const list = getList().filter((existing) => existing !== id);
    list.unshift(id);
    const capped = list.slice(0, MAX_ITEMS);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(capped));
    } catch (e) {
      // quota exceeded or storage disabled — silently ignore
    }

    // handle マップを更新。capped から外れた id の handle は掃除する。
    // handle 未指定かつ既存マップも空なら何も書かない (ID-only 利用との互換)。
    const map = getHandleMap();
    const hadMap = Object.keys(map).length > 0;
    if (handle) map[id] = String(handle);
    if (handle || hadMap) {
      const pruned = {};
      capped.forEach((pid) => {
        if (map[pid]) pruned[pid] = map[pid];
      });
      try {
        localStorage.setItem(HANDLE_KEY, JSON.stringify(pruned));
      } catch (e) {
        // ignore
      }
    }
  }

  function clear() {
    try {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(HANDLE_KEY);
    } catch (e) {
      // ignore
    }
  }

  function applySeedOverride() {
    const list = getList();
    if (list.length === 0) return;
    const seedId = list[0];
    document
      .querySelectorAll('product-recommendations[data-seed-source="recently-viewed"]')
      .forEach((el) => {
        if (el.dataset.productId !== seedId) {
          el.dataset.productId = seedId;
        }
      });
  }

  function isPdpPath() {
    // /products/<handle> または locale 配下 (/ja/products/...) も許容
    return /(^|\/)products\/[^/]+/.test(window.location.pathname);
  }

  function currentHandle() {
    const m = window.location.pathname.match(/\/products\/([^/?#]+)/);
    if (!m) return null;
    // 日本語 handle 等は pathname 上で percent-encode されている。デコードして
    // 保存し、利用側 (fetch) の encodeURIComponent で二重エンコードを防ぐ。
    try {
      return decodeURIComponent(m[1]);
    } catch (e) {
      return m[1];
    }
  }

  function recordCurrentPdp() {
    if (!isPdpPath()) return;
    const productInfo = document.querySelector('product-info[data-product-id]');
    if (productInfo) {
      record(productInfo.dataset.productId, currentHandle());
    }
  }

  function init() {
    recordCurrentPdp();
    applySeedOverride();
  }

  // defer スクリプトはパース完了後に実行されるため DOM は揃っている。
  // ただし readyState が 'loading' になり得る環境(テスト等)のため両対応。
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.LibertaRecentlyViewed = {
    STORAGE_KEY: STORAGE_KEY,
    HANDLE_KEY: HANDLE_KEY,
    MAX_ITEMS: MAX_ITEMS,
    record: record,
    getList: getList,
    getEntries: getEntries,
    clear: clear,
    applySeedOverride: applySeedOverride,
  };
})();
